import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

/**
 * One draggable row in the profile menu editor.
 *
 * WHY A HANDLE RATHER THAN A DRAGGABLE ROW
 *
 * The row is full of selects and text inputs. If the whole row were the drag
 * target, every attempt to select text in the Group field or open the Target
 * dropdown would start a drag instead. The handle is the only draggable part;
 * everything else behaves like an ordinary form control.
 *
 * WHY THE UP/DOWN BUTTONS STAY
 *
 * dnd-kit's keyboard sensor is genuinely good - tab to the handle, space to
 * lift, arrows to move, space to drop - but it is a mode you have to know
 * exists. The buttons were already here, they need no discovery, and they work
 * on a touch screen where a long-press drag inside a scrolling page is
 * awkward. Two ways to do the same thing is the right trade on an admin screen
 * that a handful of people use rarely.
 */
export default function SortableItemRow({ id, disabled, children }) {
  const {
    attributes, listeners, setNodeRef, setActivatorNodeRef,
    transform, transition, isDragging
  } = useSortable({ id, disabled });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    /* Lift the dragged row above its neighbours, and fade the gap it left.
       Without the z-index the row slides underneath the ones it passes, which
       reads as the drag having failed. */
    zIndex: isDragging ? 2 : undefined,
    opacity: isDragging ? 0.55 : undefined,
    position: isDragging ? 'relative' : undefined
  };

  return (
    <div ref={setNodeRef} style={style} className={`item-row${isDragging ? ' is-dragging' : ''}`}>
      <button
        type="button"
        ref={setActivatorNodeRef}
        className="item-drag-handle"
        disabled={disabled}
        /* The accessible name has to say what it does, not what it is. "Drag
           handle" tells a screen reader user nothing about how to use it; this
           names the interaction dnd-kit actually provides. */
        aria-label="Reorder: press space, then use the arrow keys"
        title={disabled
          ? 'Clear the filter to reorder'
          : 'Drag to reorder, or press space and use the arrow keys'}
        {...attributes}
        {...listeners}
      >
        <i className="bi bi-grip-vertical" aria-hidden="true" />
      </button>
      {children}
    </div>
  );
}
